/**
 * Widget Entry Point
 *
 * Standalone browser bundle that exposes window.renderBolderWidget()
 * Uses ReactDOM.createRoot for React 18+ compatibility
 *
 * Theme can be specified as:
 * - A preset name string: 'light', 'dark', 'modern', 'minimal', 'bold', 'ocean', 'forest', 'rose', 'midnight', 'ember', 'rounded-sm', 'sharp'
 * - A JSON object: { mode: 'dark', primaryColor: '#ff0000', ... }
 * - Via data-theme attribute (as preset name or JSON string)
 */

import { createRoot, Root } from 'react-dom/client';

import { debugLog } from './debug';
import { EmbedThemeConfig, PRESET_NAMES, THEME_PRESETS } from './theme';
import { Widget, WidgetProps, WidgetTheme } from './Widget';

// Store references to mounted widget instances
const widgetInstances = new Map<string | Element, Root>();

export interface RenderWidgetConfig extends WidgetProps {
  /** Target element - CSS selector or DOM element */
  target: string | Element;
  /** Callback when editor config changes (for WordPress integration) */
  onConfigChange?: (config: any) => void;
}

/**
 * Parse theme from string - handles both preset names and JSON objects
 */
function parseTheme(themeInput: string | object | undefined | null): WidgetTheme | undefined {
  if (!themeInput) {
    return undefined;
  }

  // Already an object - use as-is
  if (typeof themeInput === 'object') {
    return themeInput as EmbedThemeConfig;
  }

  // String - could be preset name or JSON
  if (typeof themeInput === 'string') {
    const trimmed = themeInput.trim();

    // Check if it looks like JSON (starts with {)
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        return parsed as EmbedThemeConfig;
      } catch (e) {
        console.warn('[BolderWidget] Failed to parse theme JSON:', e);
        return undefined;
      }
    }

    // Otherwise treat as preset name
    return trimmed;
  }

  return undefined;
}

/**
 * Render a Bolder widget into a target element
 *
 * @param config - Widget configuration
 * @param config.target - CSS selector or DOM element to render into
 * @param config.theme - Theme preset name or config object
 * @param config.primaryColor - Primary color (overrides theme)
 * @param config.title - Widget title
 * @param config.appBaseUrl - App base URL for API requests
 * @returns Cleanup function or null if target not found
 *
 * @example
 * // Using a preset name
 * BolderWidget.render({
 *   target: '#widget',
 *   theme: 'dark',
 *   appBaseUrl: 'https://spaces.qa.bldr.it'
 * });
 *
 * @example
 * // Using a custom theme object
 * BolderWidget.render({
 *   target: '#widget',
 *   theme: {
 *     mode: 'dark',
 *     primaryColor: '#ff6b00',
 *     borderRadius: 'lg'
 *   },
 *   appBaseUrl: 'https://spaces.qa.bldr.it'
 * });
 */
function renderBolderWidget(config: RenderWidgetConfig): (() => void) | null {
  const { target, theme, onConfigChange, ...restProps } = config;

  // Parse theme if it's a string (could be preset name or JSON)
  const parsedTheme = parseTheme(theme);

  const props = {
    ...restProps,
    theme: parsedTheme,
    onConfigChange,
  };

  debugLog('[BolderWidget] ========== RENDER DEBUG ==========');
  debugLog('[BolderWidget] Full config received:', JSON.stringify(config, null, 2));
  debugLog('[BolderWidget] Theme input:', theme);
  debugLog('[BolderWidget] Parsed theme:', parsedTheme);
  debugLog('[BolderWidget] Props being passed to Widget:', JSON.stringify(props, null, 2));
  debugLog('[BolderWidget] config.appBaseUrl:', config.appBaseUrl);

  // Resolve target element
  let targetElement: Element | null = null;

  if (typeof target === 'string') {
    targetElement = document.querySelector(target);
  } else if (target instanceof Element) {
    targetElement = target;
  }

  if (!targetElement) {
    console.error(
      `[BolderWidget] Target element not found: ${typeof target === 'string' ? target : 'Element'}`,
    );
    return null;
  }

  // Check if widget already mounted on this target
  const existingRoot = widgetInstances.get(target);
  if (existingRoot) {
    // Update existing widget
    existingRoot.render(<Widget {...props} />);
    return () => unmountBolderWidget(target);
  }

  // Create new React root and render
  const root = createRoot(targetElement);
  root.render(<Widget {...props} />);

  // Store reference
  widgetInstances.set(target, root);

  // Return unmount function
  return () => unmountBolderWidget(target);
}

/**
 * Unmount a widget from a target element
 */
function unmountBolderWidget(target: string | Element): boolean {
  const root = widgetInstances.get(target);
  if (root) {
    root.unmount();
    widgetInstances.delete(target);
    return true;
  }
  return false;
}

/**
 * Render the Widget Editor into a target element
 * The editor provides a configuration UI for the widget
 * and requires authentication (same as the main widget)
 *
 * @param config - Editor configuration
 * @param config.target - CSS selector or DOM element to render into
 * @param config.appBaseUrl - App base URL for API requests
 * @returns Cleanup function or null if target not found
 *
 * @example
 * BolderWidget.renderEditor({
 *   target: '#widget-editor',
 *   appBaseUrl: 'https://spaces.qa.bldr.it'
 * });
 */
function renderBolderWidgetEditor(
  config: Omit<RenderWidgetConfig, 'path'> & { target: string | Element },
): (() => void) | null {
  // Use the same render function but with path set to /editor
  return renderBolderWidget({
    ...config,
    path: '/editor',
  });
}

/**
 * Get all mounted widget targets
 */
function getMountedWidgets(): (string | Element)[] {
  return Array.from(widgetInstances.keys());
}

/**
 * Unmount all widgets
 */
function unmountAllBolderWidgets(): void {
  widgetInstances.forEach((root) => {
    root.unmount();
  });
  widgetInstances.clear();
}

// Expose to global window object
declare global {
  interface Window {
    BolderWidget: {
      render: typeof renderBolderWidget;
      renderEditor: typeof renderBolderWidgetEditor;
      unmount: typeof unmountBolderWidget;
      unmountAll: typeof unmountAllBolderWidgets;
      getMounted: typeof getMountedWidgets;
      /** Available theme preset names */
      presets: typeof PRESET_NAMES;
      /** Theme preset configurations */
      themes: typeof THEME_PRESETS;
    };
    // Legacy API
    renderBolderWidget: typeof renderBolderWidget;
  }
}

// Modern namespaced API
window.BolderWidget = {
  render: renderBolderWidget,
  renderEditor: renderBolderWidgetEditor,
  unmount: unmountBolderWidget,
  unmountAll: unmountAllBolderWidgets,
  getMounted: getMountedWidgets,
  presets: PRESET_NAMES,
  themes: THEME_PRESETS,
};

// Legacy API for backward compatibility
window.renderBolderWidget = renderBolderWidget;

// Auto-initialize widgets with data attributes
function autoInitialize(): void {
  const elements = document.querySelectorAll('[data-bolder-widget]');

  elements.forEach((element) => {
    // Parse theme from data-theme attribute
    // Can be: preset name (e.g., "dark", "modern") or JSON object string
    const themeAttr = element.getAttribute('data-theme');
    const theme = parseTheme(themeAttr);

    // Parse guest mode
    const guestModeAttr = element.getAttribute('data-guest-mode');
    const guestMode = guestModeAttr === 'true' || guestModeAttr === '';

    const config: RenderWidgetConfig = {
      target: element,
      theme: theme,
      title: element.getAttribute('data-title') || undefined,
      primaryColor: element.getAttribute('data-primary-color') || undefined,
      graphqlUrl: element.getAttribute('data-graphql-url') || undefined,
      apiBaseUrl: element.getAttribute('data-api-base-url') || undefined,
      appBaseUrl: element.getAttribute('data-app-base-url') || undefined,
      mediaBaseUrl: element.getAttribute('data-media-base-url') || undefined,
      assetApiUrl: element.getAttribute('data-asset-api-url') || undefined,
      path: element.getAttribute('data-path') || undefined,
      id: element.getAttribute('data-id') || undefined,
      ownerHandle: element.getAttribute('data-owner-handle') || undefined,
      guestMode,
    };

    renderBolderWidget(config);
  });
}

// Run auto-initialization when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInitialize);
} else {
  autoInitialize();
}

export {
  renderBolderWidget,
  renderBolderWidgetEditor,
  unmountBolderWidget,
  unmountAllBolderWidgets,
  getMountedWidgets,
  PRESET_NAMES,
  THEME_PRESETS,
};
export type { RenderWidgetConfig, WidgetProps, WidgetTheme, EmbedThemeConfig };
